import React, { useState, useEffect } from "react";
import {
  MagnifyingGlassIcon,
  FunnelIcon,
  PlusIcon,
  ShieldCheckIcon,
  ClockIcon,
  CurrencyDollarIcon,
  UserIcon,
  StarIcon,
  CheckBadgeIcon,
  InformationCircleIcon,
  ChatBubbleLeftRightIcon,
  ArrowsRightLeftIcon,
  BanknotesIcon,
  CreditCardIcon,
  BuildingLibraryIcon,
  DevicePhoneMobileIcon,
  PlusCircleIcon,
} from "@heroicons/react/24/outline";
import { StarIcon as StarSolidIcon } from "@heroicons/react/24/solid";
import P2POrderForm from "./P2POrderForm";
import P2PAdsList from "./P2PAdsList";
import P2PMyOrders from "./P2PMyOrders";
import MarketplaceTab from "./marketplace/MarketplaceTab";
import MarketplaceAssetForm from "./marketplace/MarketplaceAssetForm";
import useAuthStore from "../../store/useAuthStore";
import { useMarketplace } from "../../hooks/useMarketplace";

const P2PPageComponent = () => {
  const [activeTab, setActiveTab] = useState("buy");
  const [selectedAsset, setSelectedAsset] = useState("USDT");
  const [selectedFiat, setSelectedFiat] = useState("USD");
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState("all");
  const [showFilters, setShowFilters] = useState(false);
  const [showOrderForm, setShowOrderForm] = useState(false);
  const [showAssetForm, setShowAssetForm] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [amountRange, setAmountRange] = useState({ min: "", max: "" });
  const [priceRange, setPriceRange] = useState({ min: "", max: "" });
  const [refreshKey, setRefreshKey] = useState(0);

  const { user, isAuthenticated } = useAuthStore();
  const { createAsset } = useMarketplace();

  const assets = ["BTC1", "USDT", "BTC", "ETH"];
  const fiats = [
    { code: "USD", name: "US Dollar", symbol: "$" },
    { code: "EUR", name: "Euro", symbol: "€" },
    { code: "GBP", name: "British Pound", symbol: "£" },
    { code: "JPY", name: "Japanese Yen", symbol: "¥" },
    { code: "CNY", name: "Chinese Yuan", symbol: "¥" },
  ];

  const paymentMethods = [
    { id: "all", name: "All Methods", icon: CurrencyDollarIcon },
    { id: "bank_transfer", name: "Bank Transfer", icon: BuildingLibraryIcon },
    { id: "credit_card", name: "Credit Card", icon: CreditCardIcon },
    { id: "debit_card", name: "Debit Card", icon: CreditCardIcon },
    { id: "paypal", name: "PayPal", icon: BanknotesIcon },
    { id: "wise", name: "Wise", icon: BanknotesIcon },
    { id: "revolut", name: "Revolut", icon: DevicePhoneMobileIcon },
    { id: "cash", name: "Cash", icon: BanknotesIcon },
  ];

  const mainTabs = [
    {
      id: "buy",
      label: "Buy Crypto",
      description: "Buy cryptocurrency with fiat",
    },
    {
      id: "sell",
      label: "Sell Crypto",
      description: "Sell cryptocurrency for fiat",
    },
    {
      id: "my-orders",
      label: "My Orders",
      description: "View your P2P orders",
    },
    {
      id: "my-ads",
      label: "My Ads",
      description: "Manage your advertisements",
    },
  ];

  const [showMarketplace, setShowMarketplace] = useState(false);

  return (
    <div className="min-h-screen text-white mt-4 md:mt-16 px-4 sm:px-6 lg:px-8">
      {/* Header */}
      <div className="bg-[#2A356D] border border-blue-400 rounded-2xl   sm:mt-4  relative overflow-hidden py-4 px-10">
        <div className="mx-auto px-4 sm:px-6 py-4 sm:py-6">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0">
            <div className="mb-4 lg:mb-0">
              <div className="flex items-center space-x-2 sm:space-x-3 mb-2">
                <div className="w-10 h-10 sm:w-12 sm:h-12 bg-[#0E1696] rounded-xl flex items-center justify-center">
                  <ArrowsRightLeftIcon className="h-5 w-5 sm:h-6 sm:w-6 text-[#010615]" />
                </div>
                <div>
                  <h1 className="text-xl sm:text-2xl font-bold text-white">
                    P2P Trading
                  </h1>
                  <p className="text-sm sm:text-base text-white">
                    Trade crypto directly with other users
                  </p>
                </div>
              </div>

              {/* Quick Info */}
              <div className="flex flex-col sm:flex-row sm:items-center space-y-2 sm:space-y-0 sm:space-x-4 lg:space-x-6 text-xs sm:text-sm">
                <div className="flex items-center space-x-2 text-white">
                  <ShieldCheckIcon className="h-3 w-3 sm:h-4 sm:w-4" />
                  <span>Escrow Protected</span>
                </div>
                <div className="flex items-center space-x-2 text-white">
                  <ClockIcon className="h-3 w-3 sm:h-4 sm:w-4" />
                  <span>24/7 Support</span>
                </div>
                <div className="flex items-center space-x-2 text-white">
                  <CurrencyDollarIcon className="h-3 w-3 sm:h-4 sm:w-4" />
                  <span>0% Trading Fees</span>
                </div>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row items-stretch sm:items-center space-y-2 sm:space-y-0 sm:space-x-3 lg:space-x-4">
              {isAuthenticated && (
                <>
                  <button
                    onClick={() => setShowOrderForm(true)}
                    className="group flex items-center justify-center px-4 sm:px-6 py-3 bg-[#2A356D]  rounded-full transition-all duration-300   border !border-white   transform hover:scale-105"
                  >
                    <PlusCircleIcon className="h-4 w-4 sm:h-5 sm:w-5 mr-2 !text-whitegroup-hover:animate-bounce" />
                    <span className="font-bold !text-whitetext-sm  sm:text-base">
                      Create Crypto Ad
                    </span>
                  </button>

                  <button
                    onClick={() => {
                      setShowAssetForm(true);
                      setShowMarketplace(true);
                    }}
                    className="group flex items-center justify-center px-4 sm:px-6 py-3 bg-[#2A356D]  rounded-full transition-all duration-300   border !border-white   transform hover:scale-105"
                  >
                    <PlusCircleIcon className="h-4 w-4 sm:h-5 sm:w-5 mr-2 !text-whitegroup-hover:animate-bounce" />
                    <span className="hidden sm:inline !text-white">Create Asset Ad</span>
                    <span className="sm:hidden !text-white"> Asset</span>
                  </button>
                </>
              )}


            </div>
          </div>
        </div>
      </div>

      <div className="sm:py-6 py-4">
        {/* Featured Marketplace Section */}
        <div className="bg-[#2A356D] border border-blue-400 rounded-2xl shadow-2xl overflow-hidden  transition-all mb-6">
          <div className="p-6 sm:p-8">
            <div className="flex flex-col lg:flex-row items-center justify-between space-y-6 lg:space-y-0 lg:space-x-8">
              {/* Left Side - Info */}
              <div className="flex-1 text-center lg:text-left">
                <div className="inline-flex items-center space-x-3 mb-4">
                  <div>
                    <h2 className="text-2xl sm:text-3xl font-bold text-white">
                      Assets Marketplace
                    </h2>
                    <p className="text-purple-300 text-sm">
                      Trade BTC1 for Real-World Assets
                    </p>
                  </div>
                </div>

                <p className="text-white text-sm sm:text-base mb-4">
                  Buy and sell physical assets like cars, yachts, real estate,
                  and luxury items using BTC1 with secure escrow protection and
                  admin verification.
                </p>

                <div className="flex flex-wrap gap-4 justify-center lg:justify-start text-sm">
                  <div className="flex items-center space-x-2 text-green-400">
                    <ShieldCheckIcon className="h-5 w-5" />
                    <span>Admin Escrow Protected</span>
                  </div>
                  <div className="flex items-center space-x-2 text-blue-400">
                    <CheckBadgeIcon className="h-5 w-5" />
                    <span>Verified Transfers</span>
                  </div>
                </div>
              </div>

              {/* Right Side - CTA Buttons */}
              <div className="flex flex-col sm:flex-row gap-4 lg:flex-col">
                <button
                  onClick={() => setShowMarketplace(!showMarketplace)}
                  className={`px-8 py-4 rounded-full font-bold text-lg transition-all transform hover:scale-105 shadow-xl ${
                    showMarketplace
                      ? "bg-[#2A356D] border border-white hover:bg-[#0E1696] !text-white hover:!text-white"
                      : "bg-[#2A356D] border border-white hover:bg-[#0E1696] !text-white hover:!text-white"
                  }`}
                >
                  {showMarketplace
                    ? "← Back to P2P Trading"
                    : "Explore Marketplace"}
                </button>

                {isAuthenticated && (
                  <button
                    onClick={() => {
                      setShowAssetForm(true);
                      if (!showMarketplace) {
                        setShowMarketplace(true);
                      }
                    }}
                    className="group flex items-center justify-center px-4 sm:px-6 py-3 bg-[#2A356D]  rounded-full transition-all duration-300   border !border-white   transform hover:scale-105"
                  >
                    <PlusCircleIcon className="h-4 w-4 sm:h-5 sm:w-5 mr-2 !text-whitegroup-hover:animate-bounce" />
                    <span className="!text-white">List Your Asset</span>
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>

        {showMarketplace ? (
          /* Marketplace Content */
          <div className="bg-[#020817] rounded-lg shadow-xl p-6">
            <MarketplaceTab key={refreshKey} />
          </div>
        ) : (
          /* P2P Crypto Trading Section */
          <div className="bg-[#010615] rounded-xl border border-gray-700 overflow-hidden mb-4 sm:mb-6 shadow-2xl">
            <div className="border-b border-gray-700">
              <div className="flex flex-col sm:flex-row gap-20">
                {mainTabs.map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`relative px-4 sm:px-6 py-4 sm:py-3 text-center transition-all duration-200 ease-in-out group ${
                      activeTab === tab.id
                        ? "bg-[#010615] text-white border-b-2 border-[#2A356D] font-semibold"
                        : "text-white hover:text-white hover:bg-gray-800/30 border-b-2 border-transparent"
                    }`}
                  >
                    <div className={`text-sm sm:text-base transition-colors duration-200 ${
                      activeTab === tab.id ? "text-white" : "group-hover:text-white text-white"
                    }`}>
                      {tab.label}
                    </div>
                    
                    {/* Active tab indicator */}
                    {activeTab === tab.id && (
                      <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-[#0E1696] shadow-[0_0_8px_rgba(255,216,0,0.6)]" />
                    )}
                  </button>
                ))}
              </div>
            </div>

            {/* Tab Content */}
            <div className="p-4 sm:p-6">
              {activeTab !== "my-orders" && activeTab !== "my-ads" && (
                <>
                  {/* Asset and Fiat Selection */}
                  <div className="flex flex-col space-y-4 mb-4 sm:mb-6">
                    <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0">
                      <div className="flex flex-col sm:flex-row sm:items-center space-y-3 sm:space-y-0 sm:space-x-4">
                        <div className="w-full sm:w-auto">
                          <label className="block text-sm font-medium text-white mb-2">
                            {activeTab === "buy" ? "Buy" : "Sell"}
                          </label>
                          <div className="flex flex-wrap gap-2">
                            {assets.map((asset) => (
                              <button
                                key={asset}
                                onClick={() => setSelectedAsset(asset)}
                                className={`px-3 sm:px-4 py-2 rounded-lg font-medium transition-colors text-sm ${
                                  selectedAsset === asset
                                    ? "bg-green-600 text-white"
                                    : "bg-gray-700 hover:bg-gray-600 text-white"
                                }`}
                              >
                                {asset}
                              </button>
                            ))}
                          </div>
                        </div>

                        <div className="w-full sm:w-auto">
                          <label className="block text-sm font-medium text-white mb-2">
                            {activeTab === "buy" ? "Fiat Currency " : "Receive"}
                          </label>
                          <select
                            value={selectedFiat}
                            onChange={(e) => setSelectedFiat(e.target.value)}
                            className="w-full sm:w-auto bg-gray-700 border border-gray-600 rounded-lg px-3 sm:px-4 py-2 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                          >
                            {fiats.map((fiat) => (
                              <option key={fiat.code} value={fiat.code}>
                                {fiat.symbol} {fiat.name}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>

                      {/* Search and Payment Methods */}
                      <div className="flex flex-col sm:flex-row items-stretch sm:items-center space-y-3 sm:space-y-0 sm:space-x-3 lg:space-x-4">
                        <button
                          onClick={() => setShowFilters(!showFilters)}
                          className={`flex items-center justify-center px-3 sm:px-4 py-2 sm:py-3 rounded-lg font-medium transition-colors text-sm sm:text-base ${
                            showFilters
                              ? 'bg-green-600 text-white'
                              : 'bg-gray-700 hover:bg-gray-600 text-white'
                          }`}
                        >
                          <FunnelIcon className="h-4 w-4 sm:h-5 sm:w-5 mr-1 sm:mr-2" />
                          Filters
                        </button>
                        <div className="relative flex-1 sm:flex-none">
                          <MagnifyingGlassIcon className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-white" />
                          <input
                            type="text"
                            placeholder="Search merchants..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="w-full sm:w-64 pl-10 pr-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                          />
                        </div>

                        <select
                          value={selectedPaymentMethod}
                          onChange={(e) =>
                            setSelectedPaymentMethod(e.target.value)
                          }
                          className="w-full sm:w-auto bg-gray-700 border border-gray-600 rounded-lg px-3 sm:px-4 py-2 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                        >
                          {paymentMethods.map((method) => (
                            <option key={method.id} value={method.id}>
                              {method.name}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                  </div>

                  {/* Advanced Filters */}
                  {showFilters && (
                    <div className="bg-gray-750 rounded-lg p-4 sm:p-6 mb-4 sm:mb-6 border border-gray-600">
                      <h3 className="text-base sm:text-lg font-semibold text-white mb-4">
                        Advanced Filters
                      </h3>
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
                        <div>
                          <label className="block text-sm font-medium text-white mb-2">
                            Amount Range ({selectedFiat})
                          </label>
                          <div className="flex space-x-2">
                            <input
                              type="number"
                              placeholder="Min"
                              value={amountRange.min}
                              onChange={(e) =>
                                setAmountRange({
                                  ...amountRange,
                                  min: e.target.value,
                                })
                              }
                              className="w-full min-w-0 flex-1 bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                            />
                            <input
                              type="number"
                              placeholder="Max"
                              value={amountRange.max}
                              onChange={(e) =>
                                setAmountRange({
                                  ...amountRange,
                                  max: e.target.value,
                                })
                              }
                              className="w-full min-w-0 flex-1 bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                            />
                          </div>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-white mb-2">
                            Price Range ({selectedFiat})
                          </label>
                          <div className="flex space-x-2">
                            <input
                              type="number"
                              placeholder="Min"
                              value={priceRange.min}
                              onChange={(e) =>
                                setPriceRange({
                                  ...priceRange,
                                  min: e.target.value,
                                })
                              }
                              className="w-full min-w-0 flex-1 bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                            />
                            <input
                              type="number"
                              placeholder="Max"
                              value={priceRange.max}
                              onChange={(e) =>
                                setPriceRange({
                                  ...priceRange,
                                  max: e.target.value,
                                })
                              }
                              className="w-full min-w-0 flex-1 bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                            />
                          </div>
                        </div>

                        <div className="flex items-end sm:col-span-2 lg:col-span-1">
                          <button
                            onClick={() => {
                              setAmountRange({ min: "", max: "" });
                              setPriceRange({ min: "", max: "" });
                              setSearchTerm("");
                              setSelectedPaymentMethod("all");
                            }}
                            className="w-full px-4 py-2 bg-gray-600 hover:bg-gray-500 text-white rounded-lg transition-colors text-sm"
                          >
                            Clear Filters
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                </>
              )}

              {/* Content based on active tab */}
              {activeTab === "my-orders" ? (
                <P2PMyOrders key={refreshKey} showAds={false} />
              ) : activeTab === "my-ads" ? (
                <P2PMyOrders key={refreshKey} showAds={true} />
              ) : (
                <P2PAdsList
                  key={refreshKey}
                  type={activeTab}
                  asset={selectedAsset}
                  fiat={selectedFiat}
                  paymentMethod={selectedPaymentMethod}
                  searchTerm={searchTerm}
                  amountRange={amountRange}
                  priceRange={priceRange}
                />
              )}
            </div>
          </div>
        )}
      </div>

      {/* Order Form Modal */}
      {showOrderForm && (
        <P2POrderForm
          onClose={() => setShowOrderForm(false)}
          onAdCreated={() => {
            // Trigger refresh of ads list by updating refresh key
            setRefreshKey((prev) => prev + 1);
            // Switch to My Orders tab to show the newly created ad
            setActiveTab("my-orders");
          }}
          type={activeTab}
          asset={selectedAsset}
          fiat={selectedFiat}
        />
      )}

      {/* Asset Form Modal */}
      {showAssetForm && (
        <MarketplaceAssetForm
          onClose={() => setShowAssetForm(false)}
          onSubmit={async (assetData) => {
            await createAsset(assetData);
            setShowAssetForm(false);
            // Switch to marketplace view to show the newly created asset
            setShowMarketplace(true);
            setRefreshKey((prev) => prev + 1);
          }}
        />
      )}
    </div>
  );
};

export default P2PPageComponent;
